/**
 * Item icons rendered from real 3D models: every item in the game is built here from smooth,
 * well-lit geometry (physically based materials, a soft studio light and a little environment
 * for metals to catch), rendered once into a 128 px picture with a dark outline so it reads on
 * any panel, and cached. One small offscreen WebGL canvas does all of them, on demand.
 *
 * The models are made from the item's data (its icon spec, the metal of a tool or weapon, what
 * kind of thing it is), so new items get a proper icon without drawing anything by hand.
 */
import * as THREE from 'three';
import { buildCrab, buildEel, buildFishItem, buildLobster } from './fish-models.js';
import { ORE_ITEM_KIND, buildOreItem } from './ore-models.js';
import { buildCarrier, buildCord, buildFittings, buildForgedTool, buildGemItem, buildHide, buildIngot, buildNecklace, buildPlate, buildRing, buildSewn, buildTanned, buildTips, buildWeapon, buildWool, buildYarn } from './craft-models.js';
import { GEAR, ITEMS, NPCS, TOOLS, isGearId, isToolId, type GearId, type IconSpec, type ItemId, type ToolLine } from '@sunderfen/shared';
import { buildCharacter } from './models.js';

const SIZE = 128;
/** By metal id (see equipment.ts): colour, and how metallic it looks. */
const METAL: [number, number, number][] = [
  [0x8a7a68, 0.35, 0.7], [0xc8843a, 0.95, 0.32], [0xa8aeb6, 0.95, 0.35], [0xc8f4ff, 0.9, 0.18], [0x6a9a4a, 0.1, 0.6], [0x8a6a3e, 0.0, 0.75],
  [0x8a96a2, 0.95, 0.28], [0x4ad0ff, 0.6, 0.12], [0xff6a2a, 0.7, 0.25], [0xe8ecf2, 1, 0.18], [0xf2c440, 1, 0.22], [0x3a5ad8, 0.95, 0.3],
  [0xc8ccd2, 0.95, 0.22], [0x6a6a72, 0.95, 0.3], [0x2a2a32, 0.9, 0.32], [0xc49cff, 0.95, 0.2], [0x8ab8b0, 0.95, 0.25], [0xeef0f6, 1, 0.15],
  // sewn: leather, furred, studded, wolfhide, boarhide, lurkerhide, chitin, wraithweave, moonhide
  [0x9a6a3e, 0.0, 0.7], [0xb89a74, 0.0, 0.7], [0x8a5a32, 0.25, 0.7], [0x7a7a80, 0.0, 0.7], [0x5e4028, 0.0, 0.7], [0x4a6a4e, 0.0, 0.7], [0x7a6450, 0.0, 0.45], [0xc8cce8, 0.0, 0.45], [0xdce8ff, 0.0, 0.45],
];
const hex = (c: string | number | undefined, fb = 0xb0a890) => (c === undefined ? fb : typeof c === 'number' ? c : new THREE.Color(c).getHex());

/* ---------------- materials ---------------- */
const mats = new Map<string, THREE.Material>();
function std(color: number, rough = 0.6, metal = 0, extra: THREE.MeshPhysicalMaterialParameters = {}) {
  const key = `${color}|${rough}|${metal}|${JSON.stringify(extra)}`;
  let m = mats.get(key);
  if (!m) { m = new THREE.MeshPhysicalMaterial({ color, roughness: rough, metalness: metal, ...extra }); mats.set(key, m); }
  return m;
}
const metalMat = (id: number | undefined) => { const [c, met, r] = METAL[id ?? 2] ?? METAL[2]; return std(c, r, met, { clearcoat: met > 0.5 ? 0.3 : 0 }); };
const WOOD = () => std(0x7a5232, 0.75);
const DARKWOOD = () => std(0x4e3420, 0.7);
const LEATHER = () => std(0x6e4428, 0.65, 0, { sheen: 0.4 });
const ROPE = () => std(0xd8c9a0, 0.9);

/* ---------------- geometry helpers ---------------- */
function mesh(geo: THREE.BufferGeometry, mat: THREE.Material, p: [number, number, number] = [0, 0, 0], r: [number, number, number] = [0, 0, 0], s: number | [number, number, number] = 1) {
  const m = new THREE.Mesh(geo, mat);
  m.position.set(...p); m.rotation.set(...r);
  if (typeof s === 'number') m.scale.setScalar(s); else m.scale.set(...s);
  return m;
}
/** A shape turned on a lathe (points are [radius, height]). */
const lathe = (pts: [number, number][], seg = 40) => new THREE.LatheGeometry(pts.map(([x, y]) => new THREE.Vector2(x, y)), seg);
/** A box with softly rounded edges. */
function rbox(w: number, h: number, d: number, r = 0.04, seg = 4) {
  const shape = new THREE.Shape(), x = -w / 2 + r, y = -h / 2 + r, W = w - 2 * r, H = h - 2 * r;
  shape.moveTo(x, y - r); shape.lineTo(x + W, y - r); shape.quadraticCurveTo(x + W + r, y - r, x + W + r, y);
  shape.lineTo(x + W + r, y + H); shape.quadraticCurveTo(x + W + r, y + H + r, x + W, y + H + r);
  shape.lineTo(x, y + H + r); shape.quadraticCurveTo(x - r, y + H + r, x - r, y + H); shape.lineTo(x - r, y); shape.quadraticCurveTo(x - r, y - r, x, y - r);
  const g = new THREE.ExtrudeGeometry(shape, { depth: Math.max(0.001, d - 2 * r), bevelEnabled: true, bevelSize: r, bevelThickness: r, bevelSegments: seg, curveSegments: seg });
  g.translate(0, 0, -(d - 2 * r) / 2); g.computeVertexNormals();
  return g;
}
/** A flat outline extruded with a bevel (blades, axe heads, hides). Points in the XY plane. */
function plate(pts: [number, number][], depth = 0.05, bevel = 0.015) {
  const sh = new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2(x, y)));
  const g = new THREE.ExtrudeGeometry(sh, { depth, bevelEnabled: true, bevelSize: bevel, bevelThickness: bevel, bevelSegments: 3, curveSegments: 16 });
  g.translate(0, 0, -depth / 2); g.computeVertexNormals();
  return g;
}
/** A tube along a smooth curve. */
const tube = (pts: [number, number, number][], r: number, seg = 48, radial = 14) => new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts.map(p => new THREE.Vector3(...p))), seg, r, radial, false);
/** A sphere pushed in and out a little (rocks, lumps, bread), the same every time for a seed. */
function lump(r: number, amount: number, seed: number, detail = 3) {
  const g = new THREE.IcosahedronGeometry(r, detail), p = g.getAttribute('position') as THREE.BufferAttribute;
  const n = (x: number, y: number, z: number) => Math.sin(x * 3.1 + seed) * Math.cos(y * 2.7 - seed * 0.7) + Math.sin(z * 3.7 + seed * 1.3) * 0.6 + Math.sin((x + y + z) * 6.1 + seed) * 0.25;
  for (let i = 0; i < p.count; i++) {
    const v = new THREE.Vector3(p.getX(i), p.getY(i), p.getZ(i)), k = 1 + n(v.x / r, v.y / r, v.z / r) * amount;
    v.multiplyScalar(k); p.setXYZ(i, v.x, v.y, v.z);
  }
  g.computeVertexNormals();
  return g;
}
const cyl = (rt: number, rb: number, h: number, seg = 32) => new THREE.CylinderGeometry(rt, rb, h, seg);
const sphere = (r: number, seg = 32) => new THREE.SphereGeometry(r, seg, Math.round(seg * 0.75));
const torus = (r: number, t: number, arc = Math.PI * 2) => new THREE.TorusGeometry(r, t, 16, 48, arc);
const seedOf = (s: string) => { let h = 7; for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) % 9973; return h / 97; };

/* ---------------- items ---------------- */
type Spec = IconSpec;
function craftedItem(id: string, ic: Spec): THREE.Object3D | null {
  switch (ic.shape) {
    case 'bar': return buildIngot(hex(ic.color), id);
    case 'fittings': return buildFittings(hex(ic.color));
    case 'bundle': if (ic.kind === 'tips') return buildTips(hex(ic.color), id); if (id === 'bowstring' || id === 'copper_wire') return buildCord(id, hex(ic.color)); return null;
    case 'hide': { if (id === 'wool') return buildWool(hex(ic.color)); if (id === 'wraith_silk') return buildWool(hex(ic.color), true); return buildHide(id) ?? buildTanned(id); }
    case 'egg': return id === 'yarn' ? buildYarn(hex(ic.color)) : null;
    case 'gem': return buildGemItem(id, hex(ic.color));
    default: return null;
  }
}
function buildItem(id: string, ic: Spec): THREE.Object3D {
  // ores are a chunk of the same rock you mine them from
  const ore = ORE_ITEM_KIND[id] ? buildOreItem(ORE_ITEM_KIND[id]) : null;
  if (ore) return ore;
  // smithing and crafting materials have their own detailed models
  const crafted = craftedItem(id, ic);
  if (crafted) return crafted;
  const g = new THREE.Group(), sd = seedOf(id);
  switch (ic.shape) {
    case 'log': {
      const bark = std(hex(ic.color), 0.85), end = std(hex(ic.end), 0.7);
      const L = new THREE.Group(); L.rotation.set(0.25, 0.5, Math.PI / 2 - 0.45); g.add(L);
      const body = lathe([[0, -0.9], [0.32, -0.9], [0.34, -0.6], [0.33, 0], [0.35, 0.6], [0.33, 0.9], [0, 0.9]], 28); L.add(mesh(body, bark));
      for (let i = 0; i < 9; i++) { const a = (i / 9) * Math.PI * 2, ridge = mesh(cyl(0.03, 0.03, 1.7, 6), std(hex(ic.color) , 0.95), [Math.cos(a) * 0.33, 0, Math.sin(a) * 0.33]); ridge.material = std(new THREE.Color(hex(ic.color)).multiplyScalar(0.75).getHex(), 0.95); L.add(ridge); }
      for (const y of [0.905, -0.905]) {
        L.add(mesh(cyl(0.31, 0.31, 0.02, 32), end, [0, y, 0]));
        for (const r of [0.2, 0.12, 0.05]) L.add(mesh(torus(r, 0.008), std(new THREE.Color(hex(ic.end)).multiplyScalar(0.72).getHex(), 0.8), [0, y + Math.sign(y) * 0.012, 0], [Math.PI / 2, 0, 0]));
      }
      L.add(mesh(cyl(0.06, 0.09, 0.25, 10), bark, [0.32, 0.25, 0], [0, 0, -1.1]));
      break;
    }
    case 'ore': {
      const c = hex(ic.color), rock = new THREE.Color(0x6f6b64).lerp(new THREE.Color(c), 0.18).getHex();
      g.add(mesh(lump(0.62, 0.18, sd, 2), std(rock, 0.85, 0.05, { flatShading: true })));
      const crystal = std(c, 0.22, 0.55, { clearcoat: 0.8, emissive: new THREE.Color(c).multiplyScalar(0.18) });
      // veins of the ore across the face, then crystals standing out of it
      for (let i = 0; i < 3; i++) g.add(mesh(tube([[-0.5 + i * 0.2, 0.3 - i * 0.25, 0.45], [-0.1 + i * 0.15, 0.05 - i * 0.2, 0.58], [0.35, -0.15 - i * 0.12, 0.45]], 0.035, 16, 6), crystal));
      for (let i = 0; i < 10; i++) {
        const a = sd + i * 1.9, b = 0.6 + Math.sin(i * 2.3 + sd) * 0.7;
        const v = new THREE.Vector3(Math.cos(a) * Math.cos(b), Math.sin(b), Math.sin(a) * Math.cos(b)).normalize();
        if (v.z < -0.3) continue;
        const k = mesh(new THREE.OctahedronGeometry(0.15 + (i % 3) * 0.05, 0), crystal, [v.x * 0.6, v.y * 0.6, v.z * 0.6]);
        k.lookAt(v.clone().multiplyScalar(3)); k.scale.set(1, 1, 1.6); g.add(k);
      }
      break;
    }
    case 'bar': {
      const m = std(hex(ic.color), 0.28, 0.95, { clearcoat: 0.4 });
      const ingot = new THREE.Group(); ingot.rotation.set(0.5, -0.5, 0); g.add(ingot);
      const geo = new THREE.CylinderGeometry(0.62, 0.8, 0.38, 4, 1); geo.rotateY(Math.PI / 4); geo.scale(1.25, 1, 0.55);
      ingot.add(mesh(geo, m));
      ingot.add(mesh(rbox(0.7, 0.03, 0.18, 0.012), std(new THREE.Color(hex(ic.color)).multiplyScalar(0.8).getHex(), 0.4, 0.9), [0, 0.2, 0]));
      break;
    }
    case 'fittings': {
      const m = std(hex(ic.color), 0.3, 0.9);
      for (const [x, y, rx] of [[-0.35, 0.25, 0.6], [0.35, 0.25, -0.4], [0, -0.3, 0.2]] as const) g.add(mesh(torus(0.3, 0.07), m, [x, y, 0], [rx, 0.4, 0]));
      break;
    }
    case 'fish': {
      // each species has its own model; anything unknown falls back to the plain fish
      const f = buildFishItem(id, ic, sd) ?? fish(hex(ic.color), ic.style, sd);
      f.scale.multiplyScalar(ic.size ?? 1); g.add(f);
      if (ic.style === 'trophy') g.add(rosette());
      break;
    }
    case 'eel': g.add(buildEel(hex(ic.color), ic.style === 'cooked')); break;
    case 'crab': { g.add(buildCrab(hex(ic.color), ic.style === 'cooked')); if (ic.style === 'trophy') g.add(rosette()); break; }
    case 'lobster': g.add(buildLobster(hex(ic.color), ic.style === 'cooked')); break;
    case 'meat': {
      const raw = hex(ic.color), cooked = ic.style === 'cooked', m = std(raw, cooked ? 0.55 : 0.4, 0, { clearcoat: cooked ? 0.5 : 0.3 });
      if (ic.style === 'leg' || ic.style === 'ham') {
        g.add(mesh(lump(0.5, 0.06, sd), m, [-0.15, 0.05, 0], [0, 0, 0.5], [1.15, 0.85, 0.85]));
        const bone = std(0xf0e8d8, 0.5);
        g.add(mesh(cyl(0.07, 0.07, 0.7, 16), bone, [0.42, -0.32, 0], [0, 0, 0.9]));
        for (const d of [-0.07, 0.07]) g.add(mesh(sphere(0.1), bone, [0.7, -0.52 + d, d]));
      } else {
        g.add(mesh(lump(0.55, 0.1, sd, 4), m, [0, 0, 0], [0.2, 0, 0.3], [1.3, 0.55, 0.9]));
        g.add(mesh(tube([[-0.4, 0.15, 0.3], [0, 0.22, 0.38], [0.4, 0.12, 0.3]], 0.04, 24, 8), std(0xf4e4d4, 0.5), [0, 0, 0]));
      }
      if (cooked) g.add(grillMarks(0.8));
      break;
    }
    case 'egg': {
      const m = std(hex(ic.color), 0.35, 0, { clearcoat: 0.6 });
      g.add(mesh(lathe(Array.from({ length: 17 }, (_, i) => { const t = i / 16, a = t * Math.PI; return [Math.sin(a) * 0.42 * (1 - 0.18 * t), -Math.cos(a) * 0.6] as [number, number]; })), m, [0, 0, 0], [0, 0, -0.3]));
      break;
    }
    case 'nest': {
      const twig = std(0x7a5a34, 0.9);
      g.add(mesh(torus(0.5, 0.18), twig, [0, -0.1, 0], [Math.PI / 2 - 0.35, 0, 0], [1, 1, 0.8]));
      for (let i = 0; i < 14; i++) { const a = i * 0.9 + sd; g.add(mesh(cyl(0.02, 0.02, 0.6, 5), std(0x9a7444, 0.9), [Math.cos(a) * 0.5, -0.1 + Math.sin(i) * 0.08, Math.sin(a) * 0.2], [0.3, a, 1.2])); }
      for (const [x, z] of [[-0.15, 0.05], [0.18, 0.1]]) g.add(mesh(sphere(0.22), std(0xe8e0cc, 0.4), [x, 0.05, z], [0, 0, 0], [0.85, 1.1, 0.85]));
      break;
    }
    case 'veg': {
      if (ic.kind === 'onion') {
        g.add(mesh(lathe([[0, -0.45], [0.3, -0.38], [0.46, -0.1], [0.4, 0.2], [0.15, 0.42], [0.05, 0.55], [0, 0.56]]), std(0xd8b8d0, 0.45, 0, { clearcoat: 0.5 })));
        for (const a of [-0.3, 0, 0.3]) g.add(mesh(tube([[0, 0.5, 0], [a * 0.5, 0.8, 0], [a * 1.2, 1.0, 0.05]], 0.035, 12, 8), std(0x5a9a3a, 0.6)));
      } else if (ic.kind === 'mushroom' || ic.kind === 'truffle') {
        if (ic.kind === 'truffle') g.add(mesh(lump(0.45, 0.2, sd, 3), std(0x4a3a30, 0.9)));
        else {
          g.add(mesh(lathe([[0, -0.5], [0.15, -0.5], [0.13, 0], [0.12, 0.15], [0, 0.15]]), std(0xeee2cc, 0.6)));
          g.add(mesh(lathe([[0, 0.55], [0.3, 0.5], [0.5, 0.3], [0.52, 0.15], [0.12, 0.1], [0, 0.1]]), std(0xc87a4a, 0.5)));
          for (let i = 0; i < 5; i++) g.add(mesh(sphere(0.05, 10), std(0xf4ead8, 0.6), [Math.cos(i * 1.3) * 0.3, 0.42 + (i % 2) * 0.05, Math.sin(i * 1.3) * 0.3 + 0.1]));
        }
      } else if (ic.kind === 'herb') {
        const stem = std(0x4a7a3a, 0.6), leaf = std(hex(ic.color, 0x6aaa4a), 0.5, 0, { side: THREE.DoubleSide });
        for (let i = 0; i < 5; i++) {
          const a = (i - 2) * 0.32;
          g.add(mesh(tube([[0, -0.6, 0], [a * 0.5, 0, 0], [a * 1.2, 0.6, 0.05 * i]], 0.025, 16, 6), stem));
          for (const t of [0.15, 0.45]) g.add(mesh(sphere(0.12, 16), leaf, [a * (0.4 + t), -0.1 + t, 0.05], [0, 0, a + 0.6], [1.6, 0.6, 0.25]));
        }
      } else {
        // a sack of flour, tied
        const sack = std(0xe8dcc0, 0.9);
        g.add(mesh(lathe([[0, -0.55], [0.45, -0.52], [0.52, -0.2], [0.45, 0.25], [0.18, 0.42], [0.12, 0.5], [0.22, 0.65], [0, 0.66]]), sack));
        g.add(mesh(torus(0.14, 0.035), ROPE(), [0, 0.47, 0], [Math.PI / 2, 0, 0]));
        g.add(mesh(rbox(0.36, 0.28, 0.02, 0.01), std(0xc8a070, 0.8), [0, -0.1, 0.47]));
      }
      break;
    }
    case 'crop': {
      if (ic.kind === 'carrot') {
        g.add(mesh(lathe([[0, -0.75], [0.05, -0.6], [0.15, -0.1], [0.2, 0.3], [0.16, 0.38], [0, 0.4]], 32), std(0xe0863c, 0.55), [0, 0, 0], [0, 0, 0.5]));
        for (let i = 0; i < 4; i++) g.add(mesh(tube([[0.18, 0.32, 0], [0.3 + i * 0.05, 0.6, (i - 1.5) * 0.08], [0.5 + i * 0.08, 0.85, (i - 1.5) * 0.15]], 0.025, 12, 6), std(0x5a9a3a, 0.6)));
      } else if (ic.kind === 'pumpkin') {
        const skin = std(0xe07a2a, 0.45, 0, { clearcoat: 0.3 });
        for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; g.add(mesh(sphere(0.32, 24), skin, [Math.cos(a) * 0.25, -0.05, Math.sin(a) * 0.25], [0, -a, 0], [0.6, 0.85, 1])); }
        g.add(mesh(cyl(0.05, 0.07, 0.25, 10), std(0x6a7a3a, 0.7), [0, 0.4, 0], [0, 0, 0.3]));
      } else if (ic.kind === 'bean') {
        for (let i = 0; i < 3; i++) g.add(mesh(new THREE.CapsuleGeometry(0.1, 0.7, 6, 12), std(0x7aa84a, 0.5, 0, { clearcoat: 0.3 }), [(i - 1) * 0.22, 0, 0], [0, 0, 0.3 + (i - 1) * 0.2]));
      } else if (ic.kind === 'pear') {
        g.add(mesh(lathe([[0, -0.55], [0.38, -0.45], [0.45, -0.2], [0.3, 0.1], [0.2, 0.35], [0.1, 0.48], [0, 0.5]], 40), std(0xc8c050, 0.45, 0, { clearcoat: 0.4 })));
        g.add(mesh(cyl(0.025, 0.02, 0.2, 6), std(0x6a4a2a, 0.7), [0, 0.58, 0], [0, 0, 0.3]));
      } else if (ic.kind === 'cherries') {
        for (const s2 of [-1, 1]) { g.add(mesh(sphere(0.24, 24), std(0xa81a2a, 0.3, 0, { clearcoat: 0.8 }), [s2 * 0.25, -0.3, 0])); g.add(mesh(tube([[s2 * 0.25, -0.1, 0], [s2 * 0.1, 0.3, 0], [0, 0.6, 0]], 0.018, 12, 5), std(0x5a7a3a, 0.7))); }
      } else if (ic.kind === 'berries') {
        // a sprig of glossy berries in a cluster, with two leaves
        const col = hex(ic.color ?? '#c8303a');
        const pts: [number, number, number][] = [[0, -0.25, 0.1], [-0.2, -0.15, 0], [0.2, -0.15, 0], [-0.1, 0.05, 0.05], [0.12, 0.05, 0.05], [0, -0.45, 0], [-0.25, -0.38, 0.02], [0.25, -0.38, 0.02], [0, 0.22, 0]];
        for (const [x, y, z] of pts) g.add(mesh(sphere(0.15, 20), std(col, 0.25, 0, { clearcoat: 0.8 }), [x, y, z]));
        g.add(mesh(tube([[0, 0.25, 0], [0.05, 0.5, 0], [0.15, 0.7, 0]], 0.02, 10, 5), std(0x5a4a2a, 0.7)));
        for (const s2 of [-1, 1]) g.add(mesh(sphere(0.2, 12), std(0x4a8a3a, 0.6), [s2 * 0.22, 0.55, -0.05], [0, 0, s2 * 0.7], [1.2, 0.35, 0.6]));
      } else if (ic.kind === 'flax') {
        // a bundle of golden stalks tied in the middle, with round seed bolls
        for (let i = 0; i < 9; i++) { const a = (i - 4) * 0.06; g.add(mesh(cyl(0.022, 0.022, 1.5, 6), std(0xc8a858, 0.7), [Math.sin(a) * 0.6, 0, (i % 3 - 1) * 0.05], [0, 0, a])); g.add(mesh(sphere(0.06, 10), std(0xb89a50, 0.6), [Math.sin(a) * 1.35, 0.76, (i % 3 - 1) * 0.05])); }
        g.add(mesh(cyl(0.13, 0.13, 0.1, 16), std(0x8a6a3a, 0.8), [0, -0.1, 0]));
      } else if (ic.kind === 'acorn') {
        g.add(mesh(lathe([[0, -0.55], [0.22, -0.4], [0.3, -0.05], [0.28, 0.1], [0, 0.1]], 32), std(0xa8783a, 0.4, 0, { clearcoat: 0.5 })));
        g.add(mesh(sphere(0.33, 24), std(0x6a5030, 0.9), [0, 0.15, 0], [0, 0, 0], [1, 0.55, 1]));
        g.add(mesh(cyl(0.03, 0.03, 0.15, 6), std(0x5a4028, 0.8), [0, 0.38, 0]));
      } else if (ic.kind === 'garlic') {
        const skin = std(0xf0e8dc, 0.45, 0, { clearcoat: 0.4 });
        for (let i = 0; i < 6; i++) { const a = i * 1.05; g.add(mesh(sphere(0.2, 20), skin, [Math.cos(a) * 0.2, -0.1, Math.sin(a) * 0.2], [0, -a, 0.3], [0.7, 1.15, 0.8])); }
        g.add(mesh(lathe([[0, -0.4], [0.3, -0.3], [0.38, -0.05], [0.25, 0.25], [0.06, 0.45], [0.04, 0.6], [0, 0.6]], 32), skin));
        for (let i = 0; i < 6; i++) g.add(mesh(cyl(0.004, 0.004, 0.18, 4), std(0xc8b898, 0.8), [Math.cos(i) * 0.08, -0.48, Math.sin(i) * 0.08], [0.3 * Math.cos(i), 0, 0.3 * Math.sin(i)]));
      } else if (ic.kind === 'saffron') {
        // a pinch of red threads in a little twist of paper
        g.add(mesh(lathe([[0, -0.4], [0.45, -0.2], [0.5, 0.05], [0.42, 0.08], [0, -0.3]], 24), std(0xe8dcc0, 0.85)));
        for (let i = 0; i < 14; i++) { const a = i * 0.9 + sd; g.add(mesh(tube([[Math.cos(a) * 0.1, -0.05, Math.sin(a) * 0.1], [Math.cos(a) * 0.25, 0.1 + (i % 3) * 0.05, Math.sin(a) * 0.25], [Math.cos(a + 0.3) * 0.38, 0.05, Math.sin(a + 0.3) * 0.38]], 0.018, 10, 5), std(0xd83a1a, 0.4, 0, { clearcoat: 0.5 }))); }
      } else if (ic.kind === 'potato' || ic.kind === 'baked_potato') {
        const baked = ic.kind === 'baked_potato';
        g.add(mesh(lump(0.45, 0.12, sd, 4), std(baked ? 0x9a6a3a : 0xc8a06a, baked ? 0.5 : 0.8), [0, 0, 0], [0, 0, 0.3], [1.35, 0.85, 0.95]));
        for (let i = 0; i < 6; i++) g.add(mesh(sphere(0.035, 8), std(0x7a5a3a, 0.9), [Math.cos(i * 2.3) * 0.4, Math.sin(i * 1.7) * 0.25, 0.32 + (i % 2) * 0.05]));
        if (baked) { g.add(mesh(sphere(0.22, 24), std(0xf4e4b0, 0.4, 0, { clearcoat: 0.5 }), [0.05, 0.3, 0.05], [0, 0, 0], [1.4, 0.35, 0.9])); g.add(mesh(rbox(0.18, 0.08, 0.16, 0.03), std(0xf0d060, 0.3, 0, { clearcoat: 0.8 }), [0.05, 0.38, 0.05])); }
      } else if (ic.kind === 'cabbage') {
        const leaf = std(0x6aa04a, 0.55, 0, { side: THREE.DoubleSide, sheen: 0.3 });
        g.add(mesh(sphere(0.42, 32), std(0xa8d07a, 0.5), [0, 0, 0]));
        for (let i = 0; i < 7; i++) { const a = i * 0.9 + sd; g.add(mesh(sphere(0.38, 24), leaf, [Math.cos(a) * 0.3, -0.1 + (i % 3) * 0.08, Math.sin(a) * 0.3], [0.4 * Math.sin(a), a, 0.6], [1, 0.25, 0.85])); }
        g.add(mesh(tube([[0, 0.3, 0.2], [0.1, 0, 0.42], [0.05, -0.3, 0.3]], 0.015, 16, 6), std(0xd8ecb8, 0.6)));
      } else {
        // a sheaf of wheat, tied at the middle
        const stalk = std(0xd8b850, 0.7), ear = std(0xe8c860, 0.55);
        for (let i = 0; i < 9; i++) {
          const a = (i - 4) * 0.08, x = (i - 4) * 0.04;
          g.add(mesh(cyl(0.012, 0.012, 1.1, 6), stalk, [x, -0.1, 0], [0, 0, a]));
          g.add(mesh(new THREE.CapsuleGeometry(0.04, 0.2, 4, 8), ear, [x - Math.sin(a) * 0.55, 0.55, (i % 3 - 1) * 0.05], [0, 0, a]));
        }
        g.add(mesh(torus(0.11, 0.03), ROPE(), [0, -0.05, 0], [Math.PI / 2, 0, 0]));
        g.rotation.z = -0.3;
      }
      break;
    }
    case 'herb': {
      // a bunch of leaves on stems, tied; grimy ones are dusted with dirt
      const leaf = std(hex(ic.color), 0.55, 0, { side: THREE.DoubleSide, sheen: 0.3 }), dirt = std(0x6a4a2a, 0.95);
      for (let i = 0; i < 6; i++) {
        const a = (i - 2.5) * 0.28;
        g.add(mesh(tube([[0, -0.65, 0], [a * 0.3, -0.1, 0.02 * i], [a * 1.1, 0.5, 0.04 * i]], 0.022, 16, 6), std(0x4a6a32, 0.6)));
        for (const t of [0.1, 0.35, 0.6]) g.add(mesh(sphere(0.13, 16), leaf, [a * (0.3 + t * 1.2), -0.15 + t * 1.1, 0.05], [0, 0, a + 0.7], [1.5, 0.55, 0.22]));
      }
      g.add(mesh(torus(0.07, 0.025), ROPE(), [0, -0.38, 0], [Math.PI / 2, 0, 0]));
      if (ic.grimy) for (let i = 0; i < 10; i++) g.add(mesh(lump(0.06, 0.3, i, 1), dirt, [Math.cos(i * 1.9) * 0.3, -0.2 + Math.sin(i * 1.3) * 0.4, 0.12]));
      break;
    }
    case 'potion': {
      // a round-bellied bottle with a cork; the liquid fills it to the doses left
      const glass = std(0xd8eef4, 0.05, 0, { transparent: true, opacity: 0.35, clearcoat: 1 });
      g.add(mesh(lathe([[0, -0.6], [0.36, -0.55], [0.46, -0.25], [0.42, 0.05], [0.18, 0.25], [0.13, 0.45], [0.15, 0.52], [0, 0.52]], 40), glass));
      if (ic.doses > 0) {
        const top = -0.58 + 0.2 * ic.doses + (ic.doses === 3 ? 0.05 : 0), liq = std(hex(ic.color), 0.2, 0, { clearcoat: 0.8, emissive: new THREE.Color(hex(ic.color)).multiplyScalar(ic.unf ? 0.05 : 0.18) });
        const prof: [number, number][] = [[0, -0.57], [0.34, -0.53], [0.43, -0.25], [0.4, 0.04]].filter(([, y]) => y < top) as [number, number][];
        prof.push([top > 0.04 ? 0.2 : 0.42 - Math.max(0, top + 0.25) * 0.1, Math.min(top, 0.2)], [0, Math.min(top, 0.2)]);
        g.add(mesh(lathe(prof, 32), liq));
        if (ic.unf) for (let i = 0; i < 3; i++) g.add(mesh(sphere(0.08, 10), std(0x4a7a32, 0.6), [Math.cos(i * 2.1) * 0.15, -0.35 + i * 0.12, Math.sin(i * 2.1) * 0.15], [0, 0, 0], [1.4, 0.4, 0.8]));
      }
      g.add(mesh(cyl(0.12, 0.1, 0.18, 16), std(0xb8905e, 0.8), [0, 0.6, 0]));
      break;
    }
    case 'scarecrow': {
      // a post and crossbar in a ragged green coat, a sacking head and a tin hat
      g.add(mesh(cyl(0.04, 0.04, 1.7, 8), std(0x6a4a2a, 0.8), [0, -0.1, 0]));
      g.add(mesh(cyl(0.035, 0.035, 1.2, 8), std(0x6a4a2a, 0.8), [0, 0.25, 0], [0, 0, Math.PI / 2]));
      g.add(mesh(lathe([[0, -0.4], [0.28, -0.4], [0.25, 0.1], [0.18, 0.3], [0, 0.32]], 20), std(0x5a6a3a, 0.9), [0, 0.0, 0]));
      for (const s2 of [-1, 1]) g.add(mesh(cyl(0.09, 0.11, 0.4, 10), std(0x5a6a3a, 0.9), [s2 * 0.38, 0.25, 0], [0, 0, Math.PI / 2]));
      g.add(mesh(sphere(0.2, 20), std(0xd8c48a, 0.9), [0, 0.52, 0]));
      g.add(mesh(cyl(0.17, 0.2, 0.2, 16), std(0x8a9298, 0.35, 0.6), [0, 0.74, 0]));
      for (const s2 of [-1, 1]) g.add(mesh(new THREE.ConeGeometry(0.05, 0.22, 6), std(0xd8c070, 0.8), [s2 * 0.66, 0.22, 0], [0, 0, s2 * 2.4]));
      break;
    }
    case 'compost': {
      // a sack rolled down, full of dark crumbly compost
      g.add(mesh(lathe([[0, -0.5], [0.48, -0.48], [0.52, 0.1], [0.5, 0.22], [0.42, 0.18], [0, 0.18]], 32), std(ic.rich ? 0xa8905e : 0xc8b080, 0.9, 0, { sheen: 0.4 })));
      g.add(mesh(lump(0.42, 0.18, sd, 3), std(hex(ic.color), 0.95), [0, 0.22, 0], [0, 0, 0], [1, 0.45, 1]));
      for (let i = 0; i < (ic.rich ? 7 : 4); i++) g.add(mesh(sphere(0.04, 8), std(ic.rich ? 0x8a6a3a : 0x6a8a3a, 0.8), [Math.cos(i * 1.7) * 0.25, 0.36, Math.sin(i * 1.7) * 0.2]));
      break;
    }
    case 'seeds': {
      if (ic.kind === 'tubers') { for (const [x, y, r] of [[-0.25, -0.15, 0.25], [0.2, -0.2, 0.22], [0, 0.18, 0.24]] as const) { g.add(mesh(lump(r, 0.12, x * 7 + sd, 3), std(0xb08a5a, 0.85), [x, y, 0], [0, 0, x], [1.3, 0.9, 1])); for (let k = 0; k < 2; k++) g.add(mesh(cyl(0.02, 0.01, 0.12, 6), std(0x9aba6a, 0.6), [x + 0.1 * k, y + r * 0.8, 0.05], [0, 0, 0.5 - k])); } break; }
      if (ic.kind === 'sets') { for (const [x, y] of [[-0.25, -0.15], [0.22, -0.18], [0, 0.15]] as const) { g.add(mesh(lathe([[0, -0.2], [0.18, -0.15], [0.22, 0.02], [0.08, 0.2], [0.02, 0.3], [0, 0.31]], 24), std(hex(ic.color), 0.5, 0, { clearcoat: 0.4 }), [x, y, 0])); } break; }
      // a paper packet, folded over, with seeds spilling out
      g.add(mesh(rbox(0.7, 0.9, 0.06, 0.03), std(0xe8dcc0, 0.85), [0, 0.05, 0], [0, 0, 0.15]));
      g.add(mesh(rbox(0.7, 0.22, 0.07, 0.03), std(0xd0c4a4, 0.85), [-0.06, 0.48, 0.01], [0, 0, 0.15]));
      g.add(mesh(sphere(0.18, 24), std(hex(ic.color), 0.6), [0.04, 0.0, 0.05], [0, 0, 0], [1, 1, 0.2]));
      for (let i = 0; i < 6; i++) g.add(mesh(sphere(0.04, 10), std(0x8a6a3a, 0.6), [0.3 + Math.cos(i) * 0.15, -0.45 + Math.sin(i * 2) * 0.06, 0.1], [0, 0, 0], [1.2, 0.8, 0.8]));
      break;
    }
    case 'bread': {
      const crust = std(0xc8884a, 0.55, 0, { clearcoat: 0.35 });
      g.add(mesh(lump(0.55, 0.04, sd), crust, [0, 0, 0], [0, 0, 0], [1.25, 0.6, 0.85]));
      for (let i = 0; i < 3; i++) g.add(mesh(cyl(0.035, 0.035, 0.4, 8), std(0xe8c890, 0.7), [-0.3 + i * 0.3, 0.31, 0.05], [Math.PI / 2, 0, 0.6]));
      break;
    }
    case 'bowl': {
      const wood = std(0x8a5a32, 0.6, 0, { clearcoat: 0.3 });
      g.add(mesh(lathe([[0, -0.32], [0.3, -0.32], [0.52, -0.1], [0.62, 0.18], [0.58, 0.2], [0.48, -0.02], [0.28, -0.22], [0, -0.22]], 48), wood, [0, 0, 0], [0.35, 0, 0]));
      const stew = mesh(cyl(0.53, 0.53, 0.02, 40), std(hex(ic.color), 0.4, 0, { clearcoat: 0.8 }), [0, 0.13, 0.05], [0.35, 0, 0]); g.add(stew);
      for (let i = 0; i < 6; i++) g.add(mesh(lump(0.07, 0.2, i), std([0xe0863c, 0x6aaa4a, 0xd8c090][i % 3], 0.6), [Math.cos(i * 1.7) * 0.3, 0.18 + Math.sin(i * 1.7) * 0.1, 0.1 + Math.sin(i * 1.7) * 0.25]));
      break;
    }
    case 'pie': {
      const crust = std(0xd8b07a, 0.6), fill = std(hex(ic.color), 0.35, 0, { clearcoat: 0.6 });
      const P = new THREE.Group(); P.rotation.x = -1.0; g.add(P);
      const disc = (r: number) => { const sh = new THREE.Shape(); if (ic.half) { sh.moveTo(-r, 0); sh.absarc(0, 0, r, Math.PI, 0, true); sh.lineTo(-r, 0); } else sh.absarc(0, 0, r, 0, Math.PI * 2, false); return sh; };
      const ex = (r: number, d: number, b: number) => { const ge = new THREE.ExtrudeGeometry(disc(r), { depth: d, bevelEnabled: true, bevelSize: b, bevelThickness: b, bevelSegments: 4, curveSegments: 48 }); ge.computeVertexNormals(); return ge; };
      P.add(mesh(ex(0.56, 0.18, 0.06), crust));
      P.add(mesh(ex(0.48, 0.02, 0.02), fill, [0, 0, 0.22]));
      if (!ic.half) for (let i = 0; i < 4; i++) P.add(mesh(rbox(0.9, 0.08, 0.03, 0.012), crust, [0, 0, 0.27], [0, 0, i * Math.PI / 4]));
      else for (let i = 0; i < 3; i++) P.add(mesh(rbox(0.5, 0.07, 0.03, 0.012), crust, [0, 0.2, 0.27], [0, 0, (i - 1) * 0.6]));
      break;
    }
    case 'hide': {
      const pts: [number, number][] = [[-0.55, 0.55], [-0.2, 0.7], [0.2, 0.7], [0.55, 0.55], [0.45, 0.1], [0.65, -0.45], [0.25, -0.7], [-0.25, -0.7], [-0.65, -0.45], [-0.45, 0.1]];
      const geo = plate(pts, 0.05, 0.035), pp = geo.getAttribute('position') as THREE.BufferAttribute;
      for (let i = 0; i < pp.count; i++) { const x = pp.getX(i), y = pp.getY(i); pp.setZ(i, pp.getZ(i) + 0.25 * (x * x) - 0.1 * y + 0.05 * Math.sin(x * 9 + y * 7)); }
      geo.computeVertexNormals();
      g.add(mesh(geo, std(new THREE.Color(hex(ic.color)).multiplyScalar(0.85).getHex(), 0.95, 0, { sheen: 0.3 }), [0, 0, 0], [-0.55, 0.35, 0.15]));
      g.add(mesh(geo, std(0xd8c8a8, 0.95), [0.02, -0.03, -0.04], [-0.55, 0.35, 0.15], 0.98));
      break;
    }
    case 'gem': g.add(gemMesh(hex(ic.color), 0.62)); break;
    case 'trinket': g.add(trinket(ic.kind, hex(ic.color), sd)); break;
    case 'bundle': {
      for (let i = 0; i < 5; i++) {
        const o = (i - 2) * 0.12;
        if (ic.kind === 'shafts') g.add(mesh(cyl(0.035, 0.035, 1.7, 12), std(hex(ic.color), 0.7), [o, 0, o * 0.5], [0, 0, 0.78]));
        else g.add(mesh(new THREE.ConeGeometry(0.16, 0.5, 4), metalMatFromColor(hex(ic.color)), [o * 2.2, (i % 2) * 0.18 - 0.05, (i % 2) * 0.1], [0.3, 0.6, -0.5 - i * 0.12]));
      }
      if (ic.kind === 'shafts') g.add(mesh(torus(0.22, 0.04), ROPE(), [0, 0, 0], [0, Math.PI / 2, 0.78]));
      break;
    }
    case 'book': {
      const B = new THREE.Group(); B.rotation.set(0.45, -0.55, 0.05); g.add(B);
      B.add(mesh(rbox(0.95, 1.2, 0.26, 0.04), std(hex(ic.color), 0.7, 0, { sheen: 0.3 })));
      B.add(mesh(rbox(0.88, 1.12, 0.22, 0.01), std(0xeae0c8, 0.9), [0.045, 0, 0]));
      B.add(mesh(rbox(0.97, 0.12, 0.28, 0.02), std(0xc8a040, 0.3, 0.9), [0, 0.15, 0]));
      break;
    }
    case 'boot': {
      const m = std(0x5a4a3a, 0.75);
      g.add(mesh(rbox(0.45, 0.85, 0.42, 0.12), m, [-0.2, 0.15, 0], [0, 0.4, 0.05]));
      g.add(mesh(rbox(0.85, 0.32, 0.42, 0.14), m, [0.12, -0.33, 0.05], [0, 0.4, 0]));
      g.add(mesh(rbox(0.9, 0.08, 0.46, 0.03), std(0x2a2018, 0.8), [0.12, -0.5, 0.05], [0, 0.4, 0]));
      g.add(mesh(lump(0.12, 0.3, 3), std(0x3a6a4a, 0.4, 0, { clearcoat: 0.8 }), [-0.25, 0.5, 0.15]));
      break;
    }
    case 'bottle': {
      const B = new THREE.Group(); B.rotation.z = -0.6; g.add(B);
      B.add(mesh(lathe([[0, -0.6], [0.3, -0.58], [0.32, 0.15], [0.12, 0.38], [0.1, 0.62], [0.12, 0.66], [0, 0.66]]), std(0x8cc8b4, 0.05, 0, { transmission: 0.6, transparent: true, opacity: 0.65, thickness: 0.3, clearcoat: 1 })));
      B.add(mesh(cyl(0.085, 0.09, 0.14, 16), std(0x8a6a44, 0.8), [0, 0.72, 0]));
      B.add(mesh(rbox(0.25, 0.5, 0.02, 0.01), std(0xefe4c8, 0.9), [0, -0.15, 0.02], [0, 0, 0.1]));
      break;
    }
    default: g.add(mesh(lump(0.5, 0.1, sd), std(0xb0a890, 0.6)));
  }
  return g;
}
const metalMatFromColor = (c: number) => std(c, 0.3, 0.9);

function fish(c: number, style: string | undefined, sd: number) {
  const f = new THREE.Group(); f.rotation.set(0.15, -0.35, 0.12);
  const burnt = style === 'burnt', cooked = style === 'cooked';
  const col = burnt ? 0x3a322c : cooked ? new THREE.Color(c).lerp(new THREE.Color(0xc87a3a), 0.45).getHex() : c;
  const skin = std(col, burnt ? 0.9 : 0.3, burnt ? 0 : 0.25, { clearcoat: burnt ? 0 : 0.9, emissive: style === 'glow' ? new THREE.Color(0x335555) : new THREE.Color(0) });
  // a streamlined body: a lathe turned on its side, flattened
  const body = lathe(Array.from({ length: 21 }, (_, i) => { const t = i / 20; return [Math.sin(t * Math.PI) ** 0.8 * 0.3 * (1 - 0.35 * t), -0.8 + t * 1.5] as [number, number]; }), 36);
  f.add(mesh(body, skin, [0, 0, 0], [0, 0, Math.PI / 2], [1, 1, 0.55]));
  const fin = std(new THREE.Color(col).multiplyScalar(0.8).getHex(), 0.5, 0, { side: THREE.DoubleSide });
  f.add(mesh(plate([[0, 0], [0.32, 0.3], [0.26, 0], [0.32, -0.3]], 0.015, 0.01), fin, [0.68, 0, 0]));
  f.add(mesh(plate([[0, 0], [0.3, 0.2], [0.42, 0]], 0.012, 0.008), fin, [-0.15, 0.24, 0], [0, 0, 0.1]));
  f.add(mesh(plate([[0, 0], [0.18, -0.14], [0.26, 0]], 0.012, 0.008), fin, [-0.1, -0.2, 0.04]));
  f.add(mesh(sphere(0.05, 16), std(0xf4f0e8, 0.2), [-0.56, 0.06, 0.12]));
  f.add(mesh(sphere(0.03, 12), std(burnt ? 0x555555 : 0x0a0a0a, 0.1, 0, { clearcoat: 1 }), [-0.58, 0.065, 0.155]));
  f.add(mesh(torus(0.1, 0.012, Math.PI), std(new THREE.Color(col).multiplyScalar(0.7).getHex(), 0.5), [-0.42, 0.0, 0.1], [0, 0.4, Math.PI / 2]));
  if (!burnt && !cooked) for (let i = 0; i < 5; i++) f.add(mesh(sphere(0.025, 8), std(new THREE.Color(col).multiplyScalar(0.6).getHex(), 0.4), [-0.25 + i * 0.12, 0.08 + Math.sin(i + sd) * 0.05, 0.15 - Math.abs(i - 2) * 0.008]));
  if (cooked) f.add(grillMarks(0.7));
  return f;
}
function grillMarks(w: number) {
  const g = new THREE.Group(), m = std(0x3a1a08, 0.8);
  for (let i = 0; i < 3; i++) g.add(mesh(rbox(0.05, w * 0.5, 0.02, 0.008), m, [-0.25 + i * 0.25, 0.02, 0.33], [0, 0, -0.6]));
  return g;
}
function rosette() {
  const g = new THREE.Group(); g.position.set(0.5, -0.38, 0.45); g.scale.setScalar(1.25);
  g.add(mesh(cyl(0.16, 0.16, 0.04, 24), std(0xf1bf55, 0.25, 1), [0, 0, 0], [Math.PI / 2, 0, 0]));
  for (const s of [-1, 1]) g.add(mesh(rbox(0.08, 0.26, 0.02, 0.01), std(0xc84a3a, 0.6), [s * 0.06, -0.2, -0.01], [0, 0, s * 0.25]));
  return g;
}
function gemMesh(c: number, r: number) {
  const geo = new THREE.CylinderGeometry(r * 0.55, r, r * 0.35, 8, 1); const top = new THREE.ConeGeometry(r, r * 1.05, 8); top.rotateX(Math.PI); top.translate(0, -r * 0.7, 0);
  const g = new THREE.Group(); g.rotation.set(1.05, 0.35, 0.25);
  const deep = new THREE.Color(c).multiplyScalar(0.7).getHex();
  const m = std(deep, 0.08, 0.3, { clearcoat: 1, flatShading: true, emissive: new THREE.Color(c).multiplyScalar(0.22), specularIntensity: 1 });
  g.add(mesh(geo, m, [0, 0.17, 0])); g.add(mesh(top, m));
  return g;
}
function trinket(kind: string, c: number, sd: number) {
  const g = new THREE.Group(), m = std(c, 0.45, 0, { clearcoat: 0.4 });
  switch (kind) {
    case 'tusk': case 'fang': g.add(mesh(tube([[-0.5, -0.5, 0], [-0.4, 0.2, 0], [0, 0.55, 0.05], [0.5, 0.6, 0]], 0.13, 32, 16), m)); g.add(mesh(new THREE.ConeGeometry(0.13, 0.3, 16), m, [0.6, 0.6, 0], [0, 0, -Math.PI / 2])); break;
    case 'wing': g.add(mesh(plate([[-0.7, 0], [-0.2, 0.25], [0.5, 0.6], [0.4, 0.15], [0.6, -0.1], [0.2, -0.1], [0.1, -0.45]], 0.03, 0.015), std(c, 0.6, 0, { side: THREE.DoubleSide }), [0, 0, 0], [-0.3, 0.3, 0])); break;
    case 'bell': g.add(mesh(lathe([[0, 0.55], [0.12, 0.5], [0.25, 0.3], [0.3, -0.1], [0.42, -0.35], [0.4, -0.4], [0, -0.38]]), std(c, 0.25, 0.95))); g.add(mesh(sphere(0.1), std(c, 0.3, 0.95), [0, -0.48, 0])); g.add(mesh(torus(0.1, 0.03), std(c, 0.3, 0.95), [0, 0.6, 0])); break;
    case 'crown': {
      const gold = std(c, 0.2, 1);
      g.add(mesh(cyl(0.5, 0.45, 0.3, 48, ), gold, [0, -0.2, 0], [0.35, 0, 0]));
      for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; g.add(mesh(new THREE.ConeGeometry(0.1, 0.35, 12), gold, [Math.cos(a) * 0.48, 0.05 + Math.sin(a) * 0.13, Math.sin(a) * 0.4], [0.35, 0, 0])); g.add(mesh(sphere(0.06, 12), std([0xe0384a, 0x3a7ae0, 0x3ac878][i % 3], 0.1, 0, { clearcoat: 1 }), [Math.cos(a) * 0.5, -0.2 + Math.sin(a) * 0.1, Math.sin(a) * 0.45 + 0.03])); }
      break;
    }
    case 'eye': g.add(mesh(sphere(0.48), std(c, 0.15, 0, { clearcoat: 1 }))); g.add(mesh(sphere(0.2), std(0x1a1a10, 0.1, 0, { clearcoat: 1 }), [0, 0, 0.36], [0, 0, 0], [0.6, 1.4, 0.4])); break;
    case 'lantern': {
      const frame = std(0x3a4a5a, 0.4, 0.8);
      g.add(mesh(cyl(0.3, 0.3, 0.7, 6), std(c, 0.1, 0, { emissive: new THREE.Color(c).multiplyScalar(0.8), transparent: true, opacity: 0.9 })));
      for (const y of [-0.38, 0.38]) g.add(mesh(cyl(0.36, 0.36, 0.06, 6), frame, [0, y, 0]));
      g.add(mesh(new THREE.ConeGeometry(0.36, 0.25, 6), frame, [0, 0.52, 0])); g.add(mesh(torus(0.12, 0.025), frame, [0, 0.72, 0]));
      break;
    }
    case 'seed': g.add(mesh(lump(0.38, 0.05, sd), m, [0, 0, 0], [0, 0, 0.3], [0.8, 1.2, 0.8])); g.add(mesh(tube([[0, 0.45, 0], [0.1, 0.7, 0], [0.35, 0.8, 0]], 0.03, 12, 6), std(0x3a6a2a, 0.6))); g.add(mesh(sphere(0.12, 16), std(0x5a9a3a, 0.5, 0, { side: THREE.DoubleSide }), [0.42, 0.8, 0], [0, 0, 0.4], [1.6, 0.5, 0.2])); break;
    case 'feather': {
      const F = new THREE.Group(); F.rotation.z = -0.7; g.add(F);
      F.add(mesh(cyl(0.015, 0.02, 1.5, 8), std(0xe8e2d4, 0.5)));
      F.add(mesh(sphere(0.5, 32), std(c, 0.7, 0, { side: THREE.DoubleSide, sheen: 1 }), [0, 0.15, 0], [0, 0, 0], [0.32, 1.25, 0.04]));
      break;
    }
    case 'foot': g.add(mesh(lump(0.35, 0.08, sd), std(c, 0.9, 0, { sheen: 1 }), [0, 0, 0], [0, 0, 0.4], [0.75, 1.4, 0.7])); g.add(mesh(cyl(0.12, 0.12, 0.12, 16), std(0xc8a040, 0.3, 1), [0.3, 0.5, 0], [0, 0, 0.4])); break;
    default: { // a coin
      const coin = std(c, 0.22, 1, { clearcoat: 0.3 });
      for (let i = 0; i < 3; i++) g.add(mesh(cyl(0.42, 0.42, 0.07, 48), coin, [i * 0.08 - 0.08, -0.2 + i * 0.075, -i * 0.03], [0.5 - i * 0.2, 0, 0.1 * i]));
      g.add(mesh(torus(0.33, 0.02), coin, [0.08, -0.03, 0.05], [0.1 + Math.PI / 2 - 0.6, 0, 0.2]));
    }
  }
  return g;
}

/* ---------------- weapons, armour, jewellery ---------------- */
function buildGear(id: GearId): THREE.Object3D {
  const d = GEAR[id], base = (d.base ?? id) as string;
  // weapons, plate, sewn armour and jewellery: the detailed models
  if (d.slot === 'weapon' && (d.model === 'sword' || d.model === 'battleaxe' || d.model === 'maul')) return buildWeapon(d);
  if (d.slot === 'head' || d.slot === 'body' || d.slot === 'legs') return buildSewn(d) ?? buildPlate(d);
  if (d.slot === 'ring') return buildRing(d, base);
  if (d.slot === 'neck') return buildNecklace(d, base);
  const m = metalMat(d.metal), g = new THREE.Group();
  const wood = d.metal === 5 ? std(0x8a6a3e, 0.7) : d.metal === 4 ? std(0x5a7a3a, 0.6) : null;
  const grip = LEATHER();
  const gemC = /ruby/.test(id) ? 0xe0384a : /emerald/.test(id) ? 0x3ac878 : /sapphire/.test(id) ? 0x3a7ae0 : /diamond/.test(id) ? 0xeef8ff : /glowstone/.test(id) ? 0x9af0c8 : /platinum/.test(id) ? 0xc8d0e0 : 0xf2c440;
  if (d.slot === 'weapon') {
    const W = new THREE.Group(); W.rotation.z = -Math.PI / 4; g.add(W);
    if (d.model === 'sword') {
      W.add(mesh(plate([[-0.07, 0], [0.07, 0], [0.065, 1.05], [0, 1.25], [-0.065, 1.05]], 0.03, 0.02), m, [0, -0.25, 0]));
      W.add(mesh(rbox(0.012, 1.0, 0.035, 0.005), std(new THREE.Color(METAL[d.metal]?.[0] ?? 0xaaaaaa).multiplyScalar(0.75).getHex(), 0.3, 0.9), [0, 0.27, 0.0]));
      W.add(mesh(rbox(0.5, 0.07, 0.1, 0.03), m, [0, -0.27, 0]));
      W.add(mesh(cyl(0.05, 0.05, 0.36, 16), grip, [0, -0.47, 0]));
      for (let i = 0; i < 4; i++) W.add(mesh(torus(0.052, 0.012), DARKWOOD(), [0, -0.36 - i * 0.08, 0], [Math.PI / 2, 0, 0]));
      W.add(mesh(sphere(0.08), m, [0, -0.68, 0]));
    } else if (d.model === 'battleaxe' || d.model === 'maul') {
      W.add(mesh(cyl(0.045, 0.05, 1.6, 16), wood ?? WOOD(), [0, -0.1, 0]));
      if (d.model === 'maul') { W.add(mesh(rbox(0.5, 0.3, 0.3, 0.06), m, [0, 0.62, 0])); for (const s of [-1, 1]) W.add(mesh(rbox(0.06, 0.34, 0.34, 0.02), std(0x3a3a3a, 0.5, 0.7), [s * 0.2, 0.62, 0])); }
      else { W.add(mesh(plate([[0, -0.12], [0.18, -0.22], [0.48, -0.32], [0.55, 0], [0.48, 0.32], [0.18, 0.22], [0, 0.12]], 0.05, 0.02), m, [0.03, 0.55, 0])); W.add(mesh(plate([[0, -0.08], [-0.25, -0.15], [-0.25, 0.15], [0, 0.08]], 0.05, 0.02), m, [-0.03, 0.55, 0])); }
      W.add(mesh(cyl(0.055, 0.055, 0.35, 16), grip, [0, -0.7, 0]));
    } else if (d.model === 'shortbow' || d.model === 'longbow') {
      const long = d.model === 'longbow', arcR = long ? 0.85 : 0.65, bowMat = wood ?? (d.metal <= 5 ? WOOD() : m);
      W.rotation.z = -Math.PI / 4;
      W.add(mesh(new THREE.TorusGeometry(arcR, 0.045, 12, 64, 1.9), bowMat, [-arcR * 0.55, 0, 0], [0, 0, -0.95]));
      const a = 0.95; W.add(mesh(cyl(0.008, 0.008, 2 * arcR * Math.sin(a), 6), std(0xf0ead8, 0.6), [-arcR * 0.55 + arcR * Math.cos(a), 0, 0]));
      W.add(mesh(cyl(0.06, 0.06, 0.22, 16), grip, [-arcR * 0.55 + arcR, 0, 0]));
    }
  } else if (d.slot === 'ammo') {
    for (let i = 0; i < 3; i++) {
      const A = new THREE.Group(); A.position.set((i - 1) * 0.22, (i - 1) * -0.05, 0); A.rotation.z = -Math.PI / 4 + (i - 1) * 0.08; g.add(A);
      A.add(mesh(cyl(0.025, 0.025, 1.3, 10), std(0xc8a070, 0.7)));
      A.add(mesh(new THREE.ConeGeometry(0.07, 0.2, 4), m, [0, 0.74, 0]));
      for (let k = 0; k < 3; k++) A.add(mesh(plate([[0, 0], [0.09, -0.06], [0.09, -0.24], [0, -0.2]], 0.005, 0.003), std(0xe8e2d4, 0.8, 0, { side: THREE.DoubleSide }), [0, -0.48, 0], [0, (k * Math.PI * 2) / 3, 0]));
    }
  } else if (d.slot === 'head') {
    g.add(mesh(new THREE.SphereGeometry(0.55, 48, 24, 0, Math.PI * 2, 0, Math.PI / 2), m, [0, -0.15, 0], [0.25, 0.4, 0], [1, 1.1, 1]));
    g.add(mesh(torus(0.55, 0.05), m, [0, -0.15, 0], [Math.PI / 2 - 0.25, 0, 0]));
    g.add(mesh(rbox(0.1, 0.5, 0.08, 0.03), m, [0.05, -0.2, 0.55], [0.25, 0.4, 0]));
    g.add(mesh(rbox(0.06, 0.25, 0.5, 0.02), m, [0, 0.45, -0.05], [0.25, 0.4, 0]));
  } else if (d.slot === 'body') {
    const T = new THREE.Group(); T.rotation.y = 0.35; g.add(T);
    T.add(mesh(lathe([[0, -0.6], [0.42, -0.58], [0.48, -0.1], [0.55, 0.3], [0.45, 0.5], [0.18, 0.56], [0, 0.56]], 40), m, [0, 0, 0], [0, 0, 0], [1, 1, 0.6]));
    for (const s of [-1, 1]) T.add(mesh(sphere(0.22), m, [s * 0.52, 0.38, 0], [0, 0, 0], [1, 0.75, 0.9]));
    T.add(mesh(torus(0.2, 0.045), std(0x3a3028, 0.6), [0, 0.53, 0], [Math.PI / 2, 0, 0]));
    T.add(mesh(rbox(0.85, 0.08, 0.6, 0.03), LEATHER(), [0, -0.38, 0]));
  } else if (d.slot === 'legs') {
    for (const s of [-1, 1]) g.add(mesh(lathe([[0, -0.6], [0.17, -0.58], [0.2, 0], [0.24, 0.45], [0, 0.45]], 28), m, [s * 0.22, -0.05, 0], [0, 0, s * 0.08]));
    g.add(mesh(rbox(0.75, 0.2, 0.4, 0.06), m, [0, 0.45, 0]));
    g.add(mesh(rbox(0.78, 0.07, 0.42, 0.02), LEATHER(), [0, 0.52, 0]));
    g.rotation.y = 0.35;
  } else if (d.slot === 'ring') {
    g.add(mesh(torus(0.38, 0.09), m, [0, -0.15, 0], [0.55, 0.25, 0]));
    g.add(mesh(cyl(0.16, 0.14, 0.12, 24), m, [0, 0.25, 0.05], [0.35, 0, 0]));
    const gm = gemMesh(gemC, 0.17); gm.position.set(0, 0.38, 0.1); g.add(gm);
  } else if (d.slot === 'neck') {
    g.add(mesh(new THREE.TorusGeometry(0.5, 0.03, 10, 64, Math.PI * 1.35), m, [0, 0.2, 0], [0, 0, -Math.PI * 1.17]));
    g.add(mesh(cyl(0.24, 0.24, 0.07, 40), m, [0, -0.38, 0.02], [Math.PI / 2 - 0.25, 0, 0]));
    const gm = gemMesh(gemC, 0.17); gm.position.set(0, -0.36, 0.1); g.add(gm);
  } else g.add(mesh(rbox(0.6, 0.8, 0.3, 0.08), m));
  return g;
}

/* ---------------- tools and supplies ---------------- */
function buildTool(line: ToolLine | string, metal: number | undefined): THREE.Object3D {
  const forged = buildForgedTool(line, metal ?? 2) ?? buildCarrier(line, metal ?? 2);
  if (forged) return forged;
  const g = new THREE.Group(), m = metalMat(metal ?? 2), W = new THREE.Group(); W.rotation.z = -Math.PI / 4; g.add(W);
  switch (line) {
    case 'axe':
      W.add(mesh(cyl(0.05, 0.058, 1.45, 16), WOOD(), [0, -0.1, 0]));
      W.add(mesh(plate([[0, -0.1], [0.32, -0.25], [0.4, 0], [0.32, 0.22], [0, 0.1]], 0.07, 0.02), m, [0.04, 0.48, 0]));
      W.add(mesh(rbox(0.16, 0.22, 0.13, 0.03), m, [-0.02, 0.48, 0]));
      break;
    case 'pick':
      W.add(mesh(cyl(0.05, 0.058, 1.45, 16), WOOD(), [0, -0.1, 0]));
      W.add(mesh(tube([[-0.55, 0.42, 0], [-0.28, 0.58, 0], [0, 0.63, 0], [0.28, 0.58, 0], [0.55, 0.42, 0]], 0.065, 32, 10), m));
      for (const s of [-1, 1]) W.add(mesh(new THREE.ConeGeometry(0.065, 0.2, 10), m, [s * 0.62, 0.35, 0], [0, 0, s * 2.2]));
      W.add(mesh(rbox(0.16, 0.18, 0.15, 0.03), m, [0, 0.62, 0]));
      break;
    case 'hoe':
      W.add(mesh(cyl(0.04, 0.046, 1.6, 14), WOOD(), [0, -0.05, 0]));
      W.add(mesh(rbox(0.42, 0.06, 0.2, 0.02), m, [0.16, 0.76, 0.05], [0.3, 0, 0]));
      W.add(mesh(cyl(0.03, 0.03, 0.18, 8), m, [0, 0.7, 0]));
      break;
    case 'can': {
      W.visible = false;
      // brass for the best can, painted tin otherwise
      const brass = metal === 1, body = brass ? std(0xc8a050, 0.3, 0.9) : std(0x5a8a6a, 0.45, 0.4), dark = brass ? std(0xa88638, 0.3, 0.9) : std(0x4a7a5a, 0.45, 0.4);
      g.add(mesh(cyl(0.38, 0.42, 0.7, 32), body, [-0.1, -0.1, 0]));
      g.add(mesh(cyl(0.04, 0.07, 0.8, 12), dark, [0.45, 0.05, 0], [0, 0, -0.9]));
      g.add(mesh(cyl(0.12, 0.05, 0.08, 16), dark, [0.76, 0.28, 0], [0, 0, -0.9]));
      g.add(mesh(tube([[-0.42, 0.15, 0], [-0.35, 0.55, 0], [0.1, 0.55, 0], [0.18, 0.25, 0]], 0.04, 24, 8), dark));
      break;
    }
    case 'shears':
      // two curved blades on a rivet, with wooden grips
      for (const s2 of [-1, 1]) {
        W.add(mesh(plate([[0, 0], [0.07, 0.1], [0.06, 0.55], [0, 0.7], [-0.03, 0.4]], 0.03, 0.01), m, [s2 * 0.02, 0.05, s2 * 0.02], [0, 0, s2 * 0.18]));
        W.add(mesh(cyl(0.05, 0.06, 0.55, 10), WOOD(), [s2 * 0.12, -0.32, 0], [0, 0, -s2 * 0.3]));
      }
      W.add(mesh(cyl(0.05, 0.05, 0.08, 12), m, [0, 0.03, 0], [Math.PI / 2, 0, 0]));
      break;
    case 'hammer':
      W.add(mesh(cyl(0.045, 0.05, 1.0, 16), WOOD(), [0, -0.1, 0]));
      W.add(mesh(rbox(0.5, 0.22, 0.22, 0.05), std(0x5c6068, 0.35, 0.9), [0.05, 0.45, 0]));
      break;
    case 'harpoon':
      W.add(mesh(cyl(0.04, 0.045, 1.6, 12), WOOD(), [0, -0.1, 0]));
      W.add(mesh(new THREE.ConeGeometry(0.08, 0.32, 12), m, [0, 0.85, 0]));
      for (const s of [-1, 1]) W.add(mesh(new THREE.ConeGeometry(0.035, 0.18, 8), m, [s * 0.06, 0.66, 0], [0, 0, s * -2.6]));
      W.add(mesh(torus(0.05, 0.015), ROPE(), [0, -0.5, 0], [Math.PI / 2, 0, 0]));
      break;
    case 'net':
      W.add(mesh(cyl(0.035, 0.04, 1.1, 12), WOOD(), [0, -0.4, 0]));
      W.add(mesh(torus(0.38, 0.03), WOOD(), [0, 0.5, 0], [0.5, 0, 0]));
      W.add(mesh(new THREE.SphereGeometry(0.37, 16, 10, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), std(0xd8c9a8, 0.9, 0, { wireframe: true }), [0, 0.5, 0], [0.5, 0, 0], [1, 1.4, 1]));
      break;
    case 'rod': case 'flyrod':
      W.add(mesh(cyl(0.012, 0.035, 1.8, 12), std(line === 'rod' ? 0x6b4a2b : 0xb08a52, 0.5, 0, { clearcoat: 0.6 }), [0, 0.05, 0]));
      W.add(mesh(cyl(0.05, 0.05, 0.35, 16), LEATHER(), [0, -0.55, 0]));
      W.add(mesh(cyl(0.11, 0.11, 0.06, 24), std(0x3a3a3a, 0.3, 0.8), [0.1, -0.42, 0], [0, 0, Math.PI / 2]));
      W.add(mesh(tube([[0, 0.95, 0], [0.2, 0.7, 0.05], [0.25, 0.2, 0.05]], 0.004, 24, 4), std(0xeeeeee, 0.4)));
      W.add(mesh(sphere(0.06, 16), std(line === 'flyrod' ? 0xe06a3a : 0xc33333, 0.3), [0.25, 0.15, 0.05]));
      break;
    case 'trap': case 'pot': {
      const T = new THREE.Group(); T.rotation.set(0.4, -0.5, 0); g.add(T); W.visible = false;
      const cage = std(line === 'trap' ? 0xa07a44 : 0x6b5a44, 0.8);
      if (line === 'trap') { T.add(mesh(new THREE.CylinderGeometry(0.32, 0.32, 1.0, 16, 1, true), std(0xa07a44, 0.8, 0, { wireframe: true }), [0, 0, 0], [0, 0, Math.PI / 2])); for (const x of [-0.5, 0.5]) T.add(mesh(torus(0.32, 0.03), cage, [x, 0, 0], [0, Math.PI / 2, 0])); T.add(mesh(new THREE.ConeGeometry(0.3, 0.3, 16, 1, true), cage, [-0.4, 0, 0], [0, 0, -Math.PI / 2])); }
      else { T.add(mesh(new THREE.BoxGeometry(1, 0.6, 0.7, 6, 4, 4), std(0x8a7a5a, 0.8, 0, { wireframe: true }))); T.add(mesh(rbox(1.02, 0.06, 0.72, 0.02), cage, [0, -0.3, 0])); T.add(mesh(tube([[-0.3, 0.3, 0], [0, 0.65, 0], [0.3, 0.3, 0]], 0.025, 16, 6), ROPE())); }
      break;
    }
    case 'crucible': W.visible = false; g.add(mesh(lathe([[0, -0.4], [0.32, -0.4], [0.42, 0.3], [0.46, 0.35], [0.38, 0.35], [0.3, -0.3], [0, -0.3]]), std(0xb5653a, 0.6))); g.add(mesh(cyl(0.38, 0.38, 0.02, 32), std(0xffb347, 0.3, 0, { emissive: new THREE.Color(0xff8a20) }), [0, 0.28, 0])); g.rotation.x = 0.35; break;
    case 'skillet': W.rotation.z = -0.3; W.add(mesh(lathe([[0, -0.06], [0.45, -0.06], [0.5, 0.06], [0.46, 0.06], [0.42, -0.02], [0, -0.02]]), std(0x3a3a3e, 0.4, 0.8), [0, 0, 0], [0.5, 0, 0])); W.add(mesh(rbox(0.7, 0.07, 0.07, 0.03), std(0x2a2a2a, 0.5, 0.6), [0.8, 0.05, 0.1], [0, 0, 0])); break;
    case 'stewpot': W.visible = false; g.add(mesh(lathe([[0, -0.4], [0.45, -0.38], [0.5, 0.2], [0.42, 0.32], [0, 0.32]]), std(0x3a3a3e, 0.4, 0.8))); g.add(mesh(tube([[-0.45, 0.3, 0], [0, 0.75, 0], [0.45, 0.3, 0]], 0.02, 24, 6), std(0x5a5a5e, 0.4, 0.8))); break;
    case 'hooks': W.visible = false; g.add(mesh(cyl(0.03, 0.03, 1.3, 10), std(0xa3aab1, 0.3, 0.9), [0, 0.5, 0], [0, 0, Math.PI / 2])); for (const x of [-0.4, 0, 0.4]) g.add(mesh(tube([[x, 0.5, 0], [x, 0, 0], [x + 0.12, -0.2, 0], [x + 0.2, -0.05, 0]], 0.025, 20, 8), std(0xa3aab1, 0.3, 0.9))); break;
    case 'gauntlets': W.visible = false; for (const s of [-1, 1]) { g.add(mesh(rbox(0.38, 0.62, 0.26, 0.1), std(s < 0 ? 0x8a5a30 : 0xa06a3a, 0.75), [s * 0.22, -0.05, s * 0.05], [0, 0, s * 0.15])); g.add(mesh(cyl(0.22, 0.2, 0.3, 24), std(0x6a4224, 0.8), [s * 0.22, -0.45, s * 0.05])); } break;
    case 'pipe': W.visible = false; g.add(mesh(cyl(0.04, 0.04, 1.6, 12), std(0x5c6068, 0.35, 0.9), [0, 0, 0], [0, 0, 0.6])); g.add(mesh(cyl(0.07, 0.07, 0.35, 12), WOOD(), [0.46, -0.65, 0], [0, 0, 0.6])); g.add(mesh(sphere(0.14, 16), std(0xffb347, 0.2, 0, { emissive: new THREE.Color(0xff8a20), transmission: 0.3 }), [-0.46, 0.66, 0])); break;
    case 'needle': W.visible = false; g.add(mesh(cyl(0.012, 0.03, 1.2, 10), std(0xe8e0cc, 0.4), [0, 0, 0], [0, 0, 0.7])); g.add(mesh(tube([[-0.36, 0.5, 0], [0.1, 0.3, 0.1], [-0.1, -0.2, 0.05], [0.3, -0.5, 0]], 0.015, 32, 6), ROPE())); break;
    case 'chisel': W.add(mesh(cyl(0.05, 0.05, 0.5, 16), WOOD(), [0, -0.3, 0])); W.add(mesh(rbox(0.09, 0.6, 0.04, 0.015), m, [0, 0.25, 0])); break;
    case 'mould': W.visible = false; g.add(mesh(rbox(1.0, 0.25, 0.6, 0.05), std(0x6a6a66, 0.7), [0, 0, 0], [0.5, -0.3, 0])); g.add(mesh(rbox(0.7, 0.05, 0.3, 0.03), std(0x2a2a28, 0.8), [0, 0.12, 0.06], [0.5, -0.3, 0])); break;
    case 'sack': W.visible = false; g.add(mesh(lathe([[0, -0.55], [0.45, -0.5], [0.5, -0.1], [0.38, 0.3], [0.16, 0.42], [0.22, 0.55], [0, 0.56]]), std(0x8a5a32, 0.85, 0, { sheen: 0.5 }))); g.add(mesh(torus(0.17, 0.04), LEATHER(), [0, 0.42, 0], [Math.PI / 2, 0, 0])); for (const [x, c] of [[-0.1, 0xe0863c], [0.12, 0xb0523a]] as const) g.add(mesh(lump(0.1, 0.2, x * 10, 1), std(c, 0.5, 0.4, { flatShading: true }), [x, 0.6, 0])); break;
    case 'basket': W.visible = false; g.add(mesh(lathe([[0, -0.45], [0.42, -0.45], [0.55, 0.3], [0.52, 0.32], [0.4, -0.38], [0, -0.38]], 24), std(0xb8904a, 0.9, 0, { flatShading: true }))); for (const y of [-0.25, 0, 0.25]) g.add(mesh(torus(0.47 + y * 0.18, 0.025), std(0x8a6a32, 0.9), [0, y, 0], [Math.PI / 2, 0, 0])); for (const [x, c] of [[-0.18, 0xa2633b], [0.05, 0x6e4a2a], [0.22, 0x8a8a5a]] as const) g.add(mesh(cyl(0.09, 0.09, 0.8, 16), std(c, 0.85), [x, 0.4, 0], [0.1, 0, x])); break;
    case 'barrel': W.visible = false; g.add(mesh(lathe([[0, -0.55], [0.4, -0.55], [0.48, 0], [0.4, 0.55], [0, 0.55]], 32), std(0x8a5a32, 0.7))); for (const y of [-0.4, 0.4]) g.add(mesh(torus(0.44, 0.03), metalMat(metal ?? 2), [0, y, 0], [Math.PI / 2, 0, 0])); g.add(mesh(plate([[0, 0], [-0.15, 0.3], [0, 0.22], [0.15, 0.3]], 0.03, 0.01), std(0xa9b9a2, 0.4, 0.2), [0, 0.6, 0])); break;
    default: W.add(mesh(cyl(0.05, 0.05, 1.2, 12), WOOD()));
  }
  return g;
}
function buildExtra(id: string): THREE.Object3D {
  const g = new THREE.Group();
  if (id === 'bait') g.add(mesh(tube([[-0.6, -0.2, 0], [-0.3, 0.2, 0.1], [0, -0.1, 0], [0.3, 0.25, 0], [0.55, 0.1, 0]], 0.08, 48, 12), std(0xc87a7a, 0.35, 0, { clearcoat: 0.8 })));
  else if (id === 'feathers') { g.add(trinket('feather', 0xe8e2d4, 1)); const f2 = trinket('feather', 0xd06a3a, 2); f2.position.x = 0.25; f2.rotation.z = -0.3; g.add(f2); }
  else if (id === 'pack') { g.add(mesh(rbox(0.8, 0.95, 0.45, 0.15), std(0x7a5a3a, 0.8))); g.add(mesh(rbox(0.6, 0.35, 0.1, 0.05), std(0x6a4a2a, 0.8), [0, 0.1, 0.25])); g.add(mesh(torus(0.22, 0.04, Math.PI), LEATHER(), [0, 0.47, 0])); g.rotation.y = 0.4; }
  else if (id === 'boots') return buildItem('boots', { shape: 'boot' });
  else if (id === 'charm') { g.add(mesh(cyl(0.38, 0.38, 0.08, 40), std(0xe8b04a, 0.25, 1), [0, -0.1, 0], [Math.PI / 2 - 0.3, 0, 0])); g.add(mesh(torus(0.08, 0.02), std(0xe8b04a, 0.25, 1), [0, 0.32, 0])); g.add(mesh(tube([[0, 0.38, 0], [0, 0.7, 0]], 0.01, 4, 4), ROPE())); }
  else if (id === 'lantern') g.add(trinket('lantern', 0xffd27a, 0));
  else g.add(mesh(lump(0.5, 0.1, 1), std(0xb0a890, 0.6)));
  return g;
}

/* ---------------- the studio ---------------- */
let studio: { r: THREE.WebGLRenderer; scene: THREE.Scene; cam: THREE.PerspectiveCamera; root: THREE.Group; out: HTMLCanvasElement } | null = null;
let failed = false;
function setup() {
  if (studio || failed) return studio;
  try {
    const canvas = document.createElement('canvas'); canvas.width = canvas.height = SIZE;
    const r = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, preserveDrawingBuffer: true });
    r.setPixelRatio(1); r.setSize(SIZE, SIZE, false);
    r.outputColorSpace = THREE.SRGBColorSpace; r.toneMapping = THREE.ACESFilmicToneMapping; r.toneMappingExposure = 1.15;
    const scene = new THREE.Scene();
    // a soft studio: a big warm key from the upper left, cool fill, a bright rim from behind, and a gradient room to reflect
    scene.add(new THREE.HemisphereLight(0xfff4e0, 0x3a3428, 1.1));
    const key = new THREE.DirectionalLight(0xfff0dc, 2.6); key.position.set(-2, 3, 3); scene.add(key);
    const fill = new THREE.DirectionalLight(0xbcd4ff, 0.8); fill.position.set(3, 0.5, 2); scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffffff, 1.6); rim.position.set(1, 2, -3); scene.add(rim);
    const env = document.createElement('canvas'); env.width = 256; env.height = 128;
    const e = env.getContext('2d')!, grd = e.createLinearGradient(0, 0, 0, 128);
    grd.addColorStop(0, '#fdfaf2'); grd.addColorStop(0.45, '#c9c2b2'); grd.addColorStop(0.55, '#5c564c'); grd.addColorStop(1, '#2a2620');
    e.fillStyle = grd; e.fillRect(0, 0, 256, 128);
    e.fillStyle = '#ffffff'; e.fillRect(40, 18, 60, 26); e.fillRect(170, 30, 40, 18);
    const tex = new THREE.CanvasTexture(env); tex.mapping = THREE.EquirectangularReflectionMapping; tex.colorSpace = THREE.SRGBColorSpace;
    const pm = new THREE.PMREMGenerator(r); scene.environment = pm.fromEquirectangular(tex).texture; tex.dispose(); pm.dispose();
    scene.environmentIntensity = 0.7;
    const cam = new THREE.PerspectiveCamera(24, 1, 0.1, 50);
    const root = new THREE.Group(); scene.add(root);
    const out = document.createElement('canvas'); out.width = out.height = SIZE;
    studio = { r, scene, cam, root, out };
  } catch { failed = true; }
  return studio;
}

/** Render a model to a data URL: framed to fill the icon, with a dark outline. */
function shoot(model: THREE.Object3D): string | null {
  const s = setup(); if (!s) return null;
  s.root.clear(); s.root.add(model);
  // frame it: centre the bounds, and stand the camera back far enough for the bounding sphere
  model.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(model), c = box.getCenter(new THREE.Vector3()), sz = box.getSize(new THREE.Vector3());
  model.position.sub(c);
  const r = Math.max(sz.x, sz.y) * 0.5 + sz.z * 0.08, dist = r / Math.tan((s.cam.fov * Math.PI) / 360) * 1.0;
  s.cam.position.set(0, dist * 0.18, dist); s.cam.lookAt(0, 0, 0); s.cam.updateProjectionMatrix();
  s.r.render(s.scene, s.cam);
  // outline: the picture's silhouette, darkened, stamped around it, then the picture on top
  const x = s.out.getContext('2d')!;
  x.clearRect(0, 0, SIZE, SIZE);
  const sil = document.createElement('canvas'); sil.width = sil.height = SIZE;
  const sx = sil.getContext('2d')!; sx.drawImage(s.r.domElement, 0, 0); sx.globalCompositeOperation = 'source-in'; sx.fillStyle = 'rgba(12,10,8,0.85)'; sx.fillRect(0, 0, SIZE, SIZE);
  for (let a = 0; a < 8; a++) x.drawImage(sil, Math.round(Math.cos((a * Math.PI) / 4) * 2.5), Math.round(Math.sin((a * Math.PI) / 4) * 2.5));
  x.globalAlpha = 0.35; x.drawImage(sil, 2, 4); x.globalAlpha = 1;
  x.drawImage(s.r.domElement, 0, 0);
  const url = s.out.toDataURL('image/png');
  s.root.clear();
  model.traverse((o: THREE.Object3D) => { const g = (o as THREE.Mesh).geometry as THREE.BufferGeometry | undefined; g?.dispose?.(); });
  return url;
}

/** The model of an item, centred, about one unit across (for the ground and the hand); null if it can't be built. */
export function itemModel(id: string): THREE.Object3D | null {
  try {
    let o: THREE.Object3D;
    if (isGearId(id)) o = buildGear(id);
    else if (isToolId(id)) o = buildTool(TOOLS[id].line, TOOLS[id].metal);
    else {
      const it = (ITEMS as Record<string, { icon: IconSpec } | undefined>)[id];
      if (!it) return null;
      if (it.icon.shape === 'stock') return itemModel(it.icon.id);
      o = buildItem(id, it.icon);
    }
    const wrap = new THREE.Group(); wrap.add(o); wrap.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(wrap), c = box.getCenter(new THREE.Vector3()), sz = box.getSize(new THREE.Vector3());
    o.position.sub(c); o.updateMatrix();
    const k = 1 / Math.max(0.001, sz.x, sz.y, sz.z);
    const out = new THREE.Group(); out.add(wrap); wrap.scale.setScalar(k);
    out.traverse((m: THREE.Object3D) => { if ((m as THREE.Mesh).isMesh) { m.castShadow = true; m.receiveShadow = true; } });
    return out;
  } catch { return null; }
}

/** A 3D icon for an item, a tool, a piece of gear, or a shop extra; null if WebGL isn't there (the caller draws it flat). */
export function icon3d(id: string): string | null {
  if (failed) return null;
  try {
    if (isGearId(id)) return shoot(buildGear(id));
    if (isToolId(id)) return shoot(buildTool(TOOLS[id].line, TOOLS[id].metal));
    const it = (ITEMS as Record<string, { icon: IconSpec } | undefined>)[id];
    if (it) {
      if (it.icon.shape === 'stock') return icon3d(it.icon.id);
      return shoot(buildItem(id, it.icon));
    }
    return shoot(buildExtra(id));
  } catch (e) { console.warn('icon3d failed for', id, e); return null; }
}
export type { ItemId };

/* ---------------- portraits: head and shoulders, for conversations ---------------- */
const portraits = new Map<string, string | null>();
const nameSeed = (s: string) => { let h = 0; for (const c of s.toLowerCase()) h = (h * 31 + c.charCodeAt(0)) | 0; return h; };
/** A townsperson's face as they look in the world (same seed and outfit), turned a little toward you. */
export function npcPortrait(name: string, kind: string): string | null {
  const key = `${kind}|${name}`;
  if (portraits.has(key)) return portraits.get(key)!;
  const s = setup(); if (!s) return null;
  let url: string | null = null;
  try {
    const c = buildCharacter(nameSeed(name), NPCS[kind]?.outfit);
    c.root.rotation.y = 0.38;
    s.root.clear(); s.root.add(c.root); c.root.updateMatrixWorld(true);
    const fov = s.cam.fov; s.cam.fov = 21;
    s.cam.position.set(0.22, 1.45, 1.6); s.cam.lookAt(0, 1.34, 0); s.cam.updateProjectionMatrix();
    s.r.render(s.scene, s.cam);
    url = s.r.domElement.toDataURL('image/png');
    s.cam.fov = fov; s.cam.updateProjectionMatrix(); s.root.clear();
  } catch { url = null; }
  portraits.set(key, url);
  return url;
}
